import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layouts/MainLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { reviewsAPI, applicationsAPI, usersAPI } from '../lib/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/use-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { AccessReview, Application, ReviewFinding, User } from '../types';
import { formatDate } from '../lib/utils';
import { AlertTriangle, CheckCircle, Clock, Upload, Plus, Check, X, FileText, Send, User as UserIcon } from 'lucide-react';
import Papa from 'papaparse';

interface AccountData {
  email: string;
  name?: string;
  role?: string;
  lastAccess?: string;
}

interface AppAccountList {
  appId: string;
  appName: string;
  accounts: AccountData[];
  uploadedAt?: string;
  uploadedBy?: string;
}

export const ReviewDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [review, setReview] = useState<AccessReview | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [accountLists, setAccountLists] = useState<AppAccountList[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [selectedAppForUpload, setSelectedAppForUpload] = useState<string>('');

  // Dialog states
  const [isAddFindingDialogOpen, setIsAddFindingDialogOpen] = useState(false);
  const [isPasteEmailsDialogOpen, setIsPasteEmailsDialogOpen] = useState(false);
  const [pastedEmails, setPastedEmails] = useState('');
  const [newFinding, setNewFinding] = useState({
    type: 'other' as ReviewFinding['type'],
    email: '',
    appId: '',
    description: '',
    severity: 'medium' as ReviewFinding['severity'],
  });

  useEffect(() => {
    if (id) {
      loadReview(id);
    }
  }, [id]);

  const loadReview = async (reviewId: string) => {
    try {
      setLoading(true);
      const data = await reviewsAPI.getById(reviewId);
      setReview(data);

      // Load applications for this review
      if (data?.applicationIds) {
        const apps = await applicationsAPI.getAll();
        const reviewApps = apps.filter((app) => data.applicationIds.includes(app.id));
        setApplications(reviewApps);
      }

      // Load users for anomaly detection
      const allUsers = await usersAPI.getActive();
      setUsers(allUsers);
    } catch (error) {
      console.error('Error loading review:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (appId: string) => {
    setSelectedAppForUpload(appId);
    fileInputRef.current?.click();
  };

  const handlePasteEmails = (appId: string) => {
    setSelectedAppForUpload(appId);
    setPastedEmails('');
    setIsPasteEmailsDialogOpen(true);
  };

  const handleProcessPastedEmails = () => {
    if (!pastedEmails.trim() || !selectedAppForUpload || !review) {
      toast({
        title: 'Error',
        description: 'Please paste some email addresses',
        className: 'bg-red-50 border-red-200',
      });
      return;
    }

    const app = applications.find((a) => a.id === selectedAppForUpload);
    if (!app) return;

    // Parse emails - split by newlines, commas, semicolons, or spaces
    const emailList = pastedEmails
      .split(/[\n,;\s]+/)
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email && email.includes('@'));

    if (emailList.length === 0) {
      toast({
        title: 'Error',
        description: 'No valid email addresses found',
        className: 'bg-red-50 border-red-200',
      });
      return;
    }

    const accounts: AccountData[] = emailList.map((email) => ({
      email,
    }));

    const newAccountList: AppAccountList = {
      appId: selectedAppForUpload,
      appName: app.name,
      accounts,
      uploadedAt: new Date().toISOString(),
      uploadedBy: user?.email || undefined,
    };

    setAccountLists((prev) => {
      const filtered = prev.filter((list) => list.appId !== selectedAppForUpload);
      return [...filtered, newAccountList];
    });

    toast({
      title: 'Success',
      description: `Added ${accounts.length} accounts for ${app.name}`,
      className: 'bg-green-50 border-green-200',
    });

    // Detect anomalies automatically
    detectAnomaliesForApp(selectedAppForUpload, accounts);

    setIsPasteEmailsDialogOpen(false);
    setSelectedAppForUpload('');
    setPastedEmails('');
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedAppForUpload || !review) return;

    try {
      setUploading(true);

      Papa.parse<AccountData>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const app = applications.find((a) => a.id === selectedAppForUpload);
          if (!app) return;

          const newAccountList: AppAccountList = {
            appId: selectedAppForUpload,
            appName: app.name,
            accounts: results.data,
            uploadedAt: new Date().toISOString(),
            uploadedBy: user?.email || undefined,
          };

          setAccountLists((prev) => {
            const filtered = prev.filter((list) => list.appId !== selectedAppForUpload);
            return [...filtered, newAccountList];
          });

          toast({
            title: 'Success',
            description: `Uploaded ${results.data.length} accounts for ${app.name}`,
            className: 'bg-green-50 border-green-200',
          });

          // Detect anomalies automatically
          detectAnomaliesForApp(selectedAppForUpload, results.data);
        },
        error: (error) => {
          console.error('CSV parse error:', error);
          toast({
            title: 'Error',
            description: 'Failed to parse CSV file',
            className: 'bg-red-50 border-red-200',
          });
        },
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload file',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setUploading(false);
      setSelectedAppForUpload('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const detectAnomaliesForApp = (appId: string, accounts: AccountData[]) => {
    if (!review) return;

    const app = applications.find((a) => a.id === appId);
    if (!app) return;

    const userEmails = users.map((u) => u.email.toLowerCase());
    const newFindings: ReviewFinding[] = [];

    accounts.forEach((account) => {
      const email = account.email.toLowerCase();

      // Check if account is not in directory
      if (!userEmails.includes(email)) {
        // Check for personal accounts
        const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
        const isPersonal = personalDomains.some((domain) => email.endsWith(`@${domain}`));

        if (isPersonal) {
          newFindings.push({
            type: 'personal_account',
            email: account.email,
            appId: app.id,
            appName: app.name,
            description: `Personal email account detected: ${account.email}`,
            severity: 'high',
            flaggedAt: new Date().toISOString(),
          });
        } else {
          newFindings.push({
            type: 'account_not_in_directory',
            email: account.email,
            appId: app.id,
            appName: app.name,
            description: `Account not found in corporate directory: ${account.email}`,
            severity: 'medium',
            flaggedAt: new Date().toISOString(),
          });
        }
      }

      // Check for generic accounts
      const genericPrefixes = ['admin@', 'support@', 'info@', 'contact@', 'service@'];
      if (genericPrefixes.some((prefix) => email.startsWith(prefix))) {
        newFindings.push({
          type: 'generic_account',
          email: account.email,
          appId: app.id,
          appName: app.name,
          description: `Generic account detected: ${account.email}`,
          severity: 'low',
          flaggedAt: new Date().toISOString(),
        });
      }
    });

    if (newFindings.length > 0) {
      const updatedFindings = [...(review.findings || []), ...newFindings];
      reviewsAPI.update(review.id, { findings: updatedFindings });
      setReview({ ...review, findings: updatedFindings });

      toast({
        title: 'Anomalies Detected',
        description: `Found ${newFindings.length} potential issue(s) in ${app.name}`,
        className: 'bg-yellow-50 border-yellow-200',
      });
    }
  };

  const handleAddFinding = async () => {
    if (!review || !newFinding.email || !newFinding.appId || !newFinding.description) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        className: 'bg-red-50 border-red-200',
      });
      return;
    }

    try {
      const app = applications.find((a) => a.id === newFinding.appId);
      if (!app) return;

      const finding: ReviewFinding = {
        type: newFinding.type,
        email: newFinding.email,
        appId: newFinding.appId,
        appName: app.name,
        description: newFinding.description,
        severity: newFinding.severity,
        flaggedAt: new Date().toISOString(),
      };

      const updatedFindings = [...(review.findings || []), finding];
      await reviewsAPI.update(review.id, { findings: updatedFindings });
      setReview({ ...review, findings: updatedFindings });

      toast({
        title: 'Success',
        description: 'Finding added successfully',
        className: 'bg-green-50 border-green-200',
      });

      setIsAddFindingDialogOpen(false);
      setNewFinding({
        type: 'other',
        email: '',
        appId: '',
        description: '',
        severity: 'medium',
      });
    } catch (error) {
      console.error('Error adding finding:', error);
      toast({
        title: 'Error',
        description: 'Failed to add finding',
        className: 'bg-red-50 border-red-200',
      });
    }
  };

  const handleResolveFinding = async (index: number) => {
    if (!review) return;

    try {
      const updatedFindings = [...review.findings];
      updatedFindings[index] = {
        ...updatedFindings[index],
        resolvedAt: new Date().toISOString(),
      };

      await reviewsAPI.update(review.id, { findings: updatedFindings });
      setReview({ ...review, findings: updatedFindings });

      toast({
        title: 'Success',
        description: 'Finding marked as resolved',
        className: 'bg-green-50 border-green-200',
      });
    } catch (error) {
      console.error('Error resolving finding:', error);
      toast({
        title: 'Error',
        description: 'Failed to resolve finding',
        className: 'bg-red-50 border-red-200',
      });
    }
  };

  const handleSendEmails = async () => {
    if (!review) return;

    try {
      setSendingEmails(true);
      const functions = getFunctions();
      const sendReviewEmails = httpsCallable(functions, 'sendReviewEmails');

      const result = await sendReviewEmails({ reviewId: review.id }) as any;

      if (result.data.emailsSent === 0) {
        toast({
          title: 'No Emails Sent',
          description: result.data.message || 'All admins have already completed their review',
          className: 'bg-indigo-50 border-indigo-200',
        });
      } else {
        toast({
          title: 'Success',
          description: `Notification emails sent to ${result.data.emailsSent} admin(s)`,
          className: 'bg-green-50 border-green-200',
        });
      }
    } catch (error) {
      console.error('Error sending review emails:', error);
      toast({
        title: 'Error',
        description: 'Failed to send review notification emails',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setSendingEmails(false);
    }
  };

  const handleCompleteReview = async () => {
    if (!review) return;

    // Check if at least one account list has been uploaded
    if (accountLists.length === 0) {
      toast({
        title: 'Cannot Complete Review',
        description: 'Please upload at least one account list before completing the review',
        className: 'bg-yellow-50 border-yellow-200',
      });
      return;
    }

    try {
      await reviewsAPI.update(review.id, {
        status: 'completed',
        completedAt: new Date().toISOString(),
      });

      setReview({
        ...review,
        status: 'completed',
        completedAt: new Date().toISOString(),
      });

      toast({
        title: 'Success',
        description: 'Review marked as completed',
        className: 'bg-green-50 border-green-200',
      });
    } catch (error) {
      console.error('Error completing review:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete review',
        className: 'bg-red-50 border-red-200',
      });
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div>Loading review details...</div>
      </MainLayout>
    );
  }

  if (!review) {
    return (
      <MainLayout>
        <div>Review not found</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/reviews')}
              >
                ← Back
              </Button>
            </div>
            <h1 className="text-3xl font-bold">{review.title || 'Access Review'}</h1>
            {review.description && (
              <p className="text-muted-foreground mt-2">{review.description}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {review.status !== 'completed' && isSuperAdmin && (
              <Button
                variant="outline"
                onClick={handleSendEmails}
                disabled={sendingEmails}
              >
                <Send className="h-4 w-4 mr-2" />
                {sendingEmails ? 'Sending...' : 'Send Emails'}
              </Button>
            )}
            {review.status !== 'completed' && (isSuperAdmin || applications.some(app => user?.email && app.admins.includes(user.email))) && (
              <Button
                onClick={handleCompleteReview}
                className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete Review
              </Button>
            )}
          </div>
        </div>

        {/* Review Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Status</CardTitle>
            </CardHeader>
            <CardContent>
              <Badge
                className={
                  review.status === 'completed'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30'
                    : review.status === 'in_progress'
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                }
              >
                {review.status.replace('_', ' ')}
              </Badge>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Created</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{formatDate(review.createdAt)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Scheduled</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">
                {review.scheduledDate ? formatDate(review.scheduledDate) : 'Not scheduled'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Initiated By</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{review.initiatedBy}</p>
            </CardContent>
          </Card>
        </div>

        {/* Applications with Account Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Applications & Account Lists ({applications.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {applications.map((app) => {
                const accountList = accountLists.find((list) => list.appId === app.id);

                return (
                  <div
                    key={app.id}
                    className="border rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{app.name}</h3>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className={app.status === 'active' ? 'text-green-600 border-green-600' : ''}>
                            {app.status}
                          </Badge>
                          <Badge variant="outline">{app.authType}</Badge>
                          <Badge variant="outline">{app.direction}</Badge>
                        </div>
                        {app.admins && app.admins.length > 0 && (
                          <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                            <UserIcon className="h-4 w-4" />
                            <span>Admin{app.admins.length > 1 ? 's' : ''}: {app.admins.join(', ')}</span>
                          </div>
                        )}
                      </div>
                      {review.status !== 'completed' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleFileUpload(app.id)}
                            disabled={uploading}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload CSV
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePasteEmails(app.id)}
                            disabled={uploading}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Paste Emails
                          </Button>
                        </div>
                      )}
                    </div>

                    {accountList && (
                      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                            <span className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
                              {accountList.accounts.length} accounts uploaded
                            </span>
                          </div>
                          {accountList.uploadedAt && (
                            <span className="text-xs text-indigo-600 dark:text-indigo-400">
                              {formatDate(accountList.uploadedAt)} by {accountList.uploadedBy}
                            </span>
                          )}
                        </div>
                        <div className="max-h-40 overflow-y-auto">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {accountList.accounts.slice(0, 10).map((account, idx) => (
                              <div key={idx} className="text-xs text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
                                <span className="font-mono">{account.email}</span>
                                {account.name && <span className="text-indigo-500 dark:text-indigo-400">• {account.name}</span>}
                              </div>
                            ))}
                          </div>
                          {accountList.accounts.length > 10 && (
                            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-2">
                              +{accountList.accounts.length - 10} more...
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
          </CardContent>
        </Card>

        {/* Findings */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle>Security Findings ({review.findings?.length || 0})</CardTitle>
                {review.findings && review.findings.length > 0 && (
                  <Badge variant="destructive">{review.findings.length} issues found</Badge>
                )}
              </div>
              {review.status !== 'completed' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    // Auto-select app if only one application
                    if (applications.length === 1) {
                      setNewFinding({ ...newFinding, appId: applications[0].id });
                    }
                    setIsAddFindingDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Finding
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!review.findings || review.findings.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="font-medium">No security findings</p>
                <p className="text-sm">All applications passed the security review</p>
              </div>
            ) : (
              <div className="space-y-4">
                {review.findings.map((finding, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 ${
                      finding.severity === 'high'
                        ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30'
                        : finding.severity === 'medium'
                        ? 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/30'
                        : 'border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        className={`h-5 w-5 mt-0.5 ${
                          finding.severity === 'high'
                            ? 'text-red-600'
                            : finding.severity === 'medium'
                            ? 'text-yellow-600'
                            : 'text-indigo-600'
                        }`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge
                              className={
                                finding.severity === 'high'
                                  ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/30'
                                  : finding.severity === 'medium'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
                                  : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
                              }
                            >
                              {finding.severity.toUpperCase()}
                            </Badge>
                            {finding.resolvedAt && (
                              <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30">
                                <Check className="h-3 w-3 mr-1" />
                                Resolved
                              </Badge>
                            )}
                          </div>
                          {!finding.resolvedAt && review.status !== 'completed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResolveFinding(index)}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Mark Resolved
                            </Button>
                          )}
                        </div>
                        <p className="font-medium mb-1">{finding.description}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>Account: <span className="font-mono">{finding.email}</span></span>
                          <span>•</span>
                          <span>App: {finding.appName}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Flagged on {formatDate(finding.flaggedAt)}
                        </p>
                        {finding.resolvedAt && (
                          <p className="text-xs text-green-700 mt-1">
                            Resolved on {formatDate(finding.resolvedAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        {review.completedAt && (
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-indigo-500" />
                  <div>
                    <p className="font-medium">Review Created</p>
                    <p className="text-sm text-muted-foreground">{formatDate(review.createdAt)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="font-medium">Review Completed</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(review.completedAt)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Paste Emails Dialog */}
        <Dialog open={isPasteEmailsDialogOpen} onOpenChange={setIsPasteEmailsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Paste Email Addresses</DialogTitle>
              <DialogDescription>
                Paste email addresses separated by newlines, commas, semicolons, or spaces.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="paste-emails">Email Addresses *</Label>
                <Textarea
                  id="paste-emails"
                  placeholder="user1@domain.com&#10;user2@domain.com&#10;user3@domain.com"
                  value={pastedEmails}
                  onChange={(e) => setPastedEmails(e.target.value)}
                  rows={10}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Tip: You can paste from Excel/CSV or any text format
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsPasteEmailsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleProcessPastedEmails}>
                Process Emails
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Finding Dialog */}
        <Dialog open={isAddFindingDialogOpen} onOpenChange={setIsAddFindingDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Security Finding</DialogTitle>
              <DialogDescription>
                Report a security issue or anomaly found during the review.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="finding-app">Application *</Label>
                <Select
                  value={newFinding.appId}
                  onValueChange={(value) => setNewFinding({ ...newFinding, appId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select application" />
                  </SelectTrigger>
                  <SelectContent>
                    {applications.map((app) => (
                      <SelectItem key={app.id} value={app.id}>
                        {app.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="finding-email">Email / Account *</Label>
                <Input
                  id="finding-email"
                  placeholder="user@domain.com"
                  value={newFinding.email}
                  onChange={(e) => setNewFinding({ ...newFinding, email: e.target.value })}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="finding-type">Type</Label>
                <Select
                  value={newFinding.type}
                  onValueChange={(value) => setNewFinding({ ...newFinding, type: value as ReviewFinding['type'] })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="account_not_in_directory">Account Not in Directory</SelectItem>
                    <SelectItem value="personal_account">Personal Account</SelectItem>
                    <SelectItem value="generic_account">Generic Account</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="finding-severity">Severity *</Label>
                <Select
                  value={newFinding.severity}
                  onValueChange={(value) => setNewFinding({ ...newFinding, severity: value as ReviewFinding['severity'] })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="finding-description">Description *</Label>
                <Textarea
                  id="finding-description"
                  placeholder="Describe the security issue..."
                  value={newFinding.description}
                  onChange={(e) => setNewFinding({ ...newFinding, description: e.target.value })}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddFindingDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddFinding}>Add Finding</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};
